/**
 * TabPlanificacion — Fase 2: Planificación mensual.
 * 
 * - Calendario de contenidos interactivo (FullCalendar en vista mes con Drag & Drop, creación y edición).
 * - Acciones de marketing con estados, fechas límite y creación/edición.
 */

import { useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Plus,
  Target,
  Loader2,
  Calendar as CalendarIcon,
  Clock,
  Trash2,
  X,
  Edit2,
  CheckCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import type { ContentCalendarItem, MarketingAction } from '@/types';

interface Props {
  clientId: string;
  period: string;
}

const CONTENT_TYPE_COLORS: Record<string, string> = {
  post: '#6366f1',
  reel: '#ec4899',
  story: '#f59e0b',
  carousel: '#22c55e',
  live: '#ef4444',
  blog: '#3b82f6',
  ad: '#8b5cf6',
};

const STATUS_VARIANTS: Record<string, 'secondary' | 'warning' | 'info' | 'success'> = {
  planned: 'secondary',
  in_progress: 'warning',
  completed: 'success',
  cancelled: 'secondary',
};

export function TabPlanificacion({ clientId, period }: Props) {
  const [calendar, setCalendar] = useState<ContentCalendarItem[]>([]);
  const [actions, setActions] = useState<MarketingAction[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal Contenido
  const [contentModalOpen, setContentModalOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<ContentCalendarItem | null>(null);
  const [contentForm, setContentForm] = useState({
    title: '',
    content_type: 'post',
    platform: 'Instagram',
    scheduled_date: '',
    scheduled_time: '',
    caption: '',
    visual_notes: '',
    status: 'planned' as ContentCalendarItem['status'],
  });
  const [savingContent, setSavingContent] = useState(false);

  // Modal Acción de Marketing
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [editingAction, setEditingAction] = useState<MarketingAction | null>(null);
  const [actionForm, setActionForm] = useState({
    title: '',
    action_type: 'Campaña',
    description: '',
    due_date: '',
    status: 'planned' as MarketingAction['status'],
  });
  const [savingAction, setSavingAction] = useState(false);

  const loadData = () => {
    Promise.all([
      api.getContentCalendar(clientId, period),
      api.getMarketingActions(clientId, period),
    ])
      .then(([calRes, actRes]) => {
        setCalendar(calRes.calendar as ContentCalendarItem[]);
        setActions(actRes.actions as MarketingAction[]);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [clientId, period]);

  // Transformar items para FullCalendar
  const calendarEvents = calendar.map((item) => ({
    id: item.id,
    title: `${item.content_type?.toUpperCase()}: ${item.title}`,
    date: item.scheduled_date,
    backgroundColor: CONTENT_TYPE_COLORS[item.content_type] || '#6366f1',
    borderColor: CONTENT_TYPE_COLORS[item.content_type] || '#6366f1',
    extendedProps: { item },
  }));

  // ─── Handlers Contenido ───
  const openNewContentModal = (dateStr?: string) => {
    const defaultDate = dateStr || `${period}-01`;
    setEditingContent(null);
    setContentForm({
      title: '',
      content_type: 'post',
      platform: 'Instagram',
      scheduled_date: defaultDate,
      scheduled_time: '18:00',
      caption: '',
      visual_notes: '',
      status: 'planned',
    });
    setContentModalOpen(true);
  };

  const openEditContentModal = (item: ContentCalendarItem) => {
    setEditingContent(item);
    setContentForm({
      title: item.title,
      content_type: item.content_type || 'post',
      platform: item.platform || 'Instagram',
      scheduled_date: item.scheduled_date,
      scheduled_time: item.scheduled_time || '',
      caption: item.caption || '',
      visual_notes: item.visual_notes || '',
      status: item.status || 'planned',
    });
    setContentModalOpen(true);
  };

  const handleSaveContent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contentForm.title || !contentForm.scheduled_date) return;

    setSavingContent(true);
    try {
      if (editingContent) {
        await api.updateContentCalendar(editingContent.id, {
          ...contentForm,
          client_id: clientId,
          period,
        });
      } else {
        await api.createContentCalendar({
          ...contentForm,
          client_id: clientId,
          period,
        });
      }
      setContentModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error guardando contenido:', error);
      alert('Error guardando contenido en el calendario');
    } finally {
      setSavingContent(false);
    }
  };

  const handleDeleteContent = async (id: string) => {
    if (!confirm('¿Eliminar este contenido del calendario?')) return;
    try {
      await api.deleteContentCalendar(id);
      setContentModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error eliminando contenido:', error);
    }
  };

  // Drag and drop para reagendar
  const handleEventDrop = async (info: any) => {
    const item = info.event.extendedProps.item as ContentCalendarItem;
    const newDate = info.event.startStr.split('T')[0];

    try {
      await api.updateContentCalendar(item.id, {
        ...item,
        scheduled_date: newDate,
      });
      setCalendar((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, scheduled_date: newDate } : i))
      );
    } catch (error) {
      console.error('Error reagendando contenido:', error);
      info.revert();
    }
  };

  // ─── Handlers Acciones de Marketing ───
  const openNewActionModal = () => {
    setEditingAction(null);
    setActionForm({
      title: '',
      action_type: 'Campaña',
      description: '',
      due_date: '',
      status: 'planned',
    });
    setActionModalOpen(true);
  };

  const openEditActionModal = (action: MarketingAction) => {
    setEditingAction(action);
    setActionForm({
      title: action.title,
      action_type: action.action_type || 'Campaña',
      description: action.description || '',
      due_date: action.due_date || '',
      status: action.status || 'planned',
    });
    setActionModalOpen(true);
  };

  const handleSaveAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!actionForm.title) return;

    setSavingAction(true);
    try {
      if (editingAction) {
        await api.updateMarketingAction(editingAction.id, {
          ...actionForm,
          client_id: clientId,
          period,
        });
      } else {
        await api.createMarketingAction({
          ...actionForm,
          client_id: clientId,
          period,
        });
      }
      setActionModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error guardando acción de marketing:', error);
      alert('Error guardando acción de marketing');
    } finally {
      setSavingAction(false);
    }
  };

  const handleDeleteAction = async (id: string) => {
    if (!confirm('¿Eliminar esta acción de marketing?')) return;
    try {
      await api.deleteMarketingAction(id);
      setActionModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error eliminando acción:', error);
    }
  };

  const toggleActionStatus = async (action: MarketingAction) => {
    const nextStatus: Record<string, MarketingAction['status']> = {
      planned: 'in_progress',
      in_progress: 'completed',
      completed: 'planned',
    };
    const newStatus = nextStatus[action.status] || 'planned';

    try {
      await api.updateMarketingAction(action.id, {
        ...action,
        status: newStatus,
      });
      setActions((prev) =>
        prev.map((a) => (a.id === action.id ? { ...a, status: newStatus } : a))
      );
    } catch (error) {
      console.error('Error actualizando estado:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ─── Calendario de Contenidos ─── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <CalendarIcon size={18} className="text-indigo-600" />
              Calendario de Contenidos — {period}
            </span>
            <Button size="sm" onClick={() => openNewContentModal()} className="gap-1 shadow-sm">
              <Plus size={14} />
              Nuevo Contenido
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            events={calendarEvents}
            locale="es"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: '',
            }}
            height="auto"
            editable={true}
            selectable={true}
            eventClick={(info) => {
              openEditContentModal(info.event.extendedProps.item);
            }}
            dateClick={(info) => {
              openNewContentModal(info.dateStr);
            }}
            eventDrop={handleEventDrop}
          />

          {/* Leyenda y tip de arrastre */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100">
            <div className="flex flex-wrap gap-3">
              {Object.entries(CONTENT_TYPE_COLORS).map(([type, color]) => (
                <div key={type} className="flex items-center gap-1.5 text-xs text-slate-600">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                  <span className="capitalize">{type}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 italic">
              💡 Tip: Puedes arrastrar cualquier evento a otro día para reagendarlo automáticamente.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ─── Acciones de Marketing ─── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Target size={18} className="text-pink-600" />
              Acciones de Marketing Estratégicas
            </span>
            <Button size="sm" variant="outline" onClick={openNewActionModal} className="gap-1">
              <Plus size={14} />
              Nueva Acción
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {actions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                No hay acciones de marketing planificadas para este período.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={openNewActionModal}
                className="mt-3 text-xs gap-1"
              >
                <Plus size={12} />
                Agregar la primera acción
              </Button>
            </div>
          ) : (
            <div className="space-y-2.5">
              {actions.map((action) => (
                <div
                  key={action.id}
                  className="flex items-center gap-3 rounded-xl border border-slate-200/80 p-3 hover:border-slate-300 transition-all bg-white shadow-xs group"
                >
                  <button
                    onClick={() => toggleActionStatus(action)}
                    className="p-1 rounded text-slate-400 hover:text-emerald-600 transition-colors"
                    title="Cambiar estado"
                  >
                    <CheckCircle
                      size={18}
                      className={
                        action.status === 'completed'
                          ? 'text-emerald-600 fill-emerald-100'
                          : 'text-slate-300'
                      }
                    />
                  </button>

                  <div
                    className="flex-1 cursor-pointer min-w-0"
                    onClick={() => openEditActionModal(action)}
                  >
                    <div className="flex items-center gap-2">
                      <p
                        className={`text-sm font-medium truncate ${
                          action.status === 'completed'
                            ? 'line-through text-slate-400'
                            : 'text-slate-800'
                        }`}
                      >
                        {action.title}
                      </p>
                      {action.due_date && (
                        <span className="text-[11px] text-slate-400 flex items-center gap-1 shrink-0">
                          <Clock size={11} />
                          {formatDate(action.due_date)}
                        </span>
                      )}
                    </div>
                    {action.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {action.description}
                      </p>
                    )}
                  </div>

                  <Badge variant="outline" className="text-xs shrink-0">
                    {action.action_type}
                  </Badge>

                  <button
                    onClick={() => toggleActionStatus(action)}
                    className="shrink-0"
                  >
                    <Badge variant={STATUS_VARIANTS[action.status] || 'secondary'} className="text-xs cursor-pointer">
                      {action.status === 'planned'
                        ? 'Planificada'
                        : action.status === 'in_progress'
                        ? 'En Curso'
                        : action.status === 'completed'
                        ? 'Completada ✓'
                        : action.status}
                    </Badge>
                  </button>

                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditActionModal(action)}
                      className="h-7 w-7 text-slate-400 hover:text-slate-700"
                    >
                      <Edit2 size={13} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteAction(action.id)}
                      className="h-7 w-7 text-slate-400 hover:text-rose-600"
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Modal: Crear / Editar Contenido del Calendario ─── */}
      {contentModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900 text-sm">
                {editingContent ? 'Editar Contenido de Calendario' : 'Nuevo Contenido en Calendario'}
              </h3>
              <button
                onClick={() => setContentModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveContent} className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Título del Contenido *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Reel: 3 tips de fotografía de retrato"
                  value={contentForm.title}
                  onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Formato
                  </label>
                  <select
                    value={contentForm.content_type}
                    onChange={(e) => setContentForm({ ...contentForm, content_type: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
                  >
                    <option value="post">Post (Feed)</option>
                    <option value="reel">Reel / Video Corto</option>
                    <option value="carousel">Carrusel</option>
                    <option value="story">Historia / Story</option>
                    <option value="live">Transmisión en Vivo</option>
                    <option value="ad">Anuncio / Pauta Publicitaria</option>
                    <option value="blog">Artículo / Blog</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Plataforma
                  </label>
                  <select
                    value={contentForm.platform}
                    onChange={(e) => setContentForm({ ...contentForm, platform: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
                  >
                    <option value="Instagram">Instagram</option>
                    <option value="TikTok">TikTok</option>
                    <option value="Facebook">Facebook</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="YouTube">YouTube</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Fecha de Publicación *
                  </label>
                  <input
                    type="date"
                    required
                    value={contentForm.scheduled_date}
                    onChange={(e) => setContentForm({ ...contentForm, scheduled_date: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Hora (Opcional)
                  </label>
                  <input
                    type="time"
                    value={contentForm.scheduled_time}
                    onChange={(e) => setContentForm({ ...contentForm, scheduled_time: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Notas Visuales / Guión Breve
                </label>
                <textarea
                  rows={2}
                  placeholder="Detalles sobre el gancho, estilo visual o iluminación..."
                  value={contentForm.visual_notes}
                  onChange={(e) => setContentForm({ ...contentForm, visual_notes: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Caption / Texto Borrador
                </label>
                <textarea
                  rows={3}
                  placeholder="Texto a publicar junto al contenido..."
                  value={contentForm.caption}
                  onChange={(e) => setContentForm({ ...contentForm, caption: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Estado
                </label>
                <select
                  value={contentForm.status}
                  onChange={(e) =>
                    setContentForm({
                      ...contentForm,
                      status: e.target.value as ContentCalendarItem['status'],
                    })
                  }
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
                >
                  <option value="planned">Planificado</option>
                  <option value="in_progress">En Producción / Grabación</option>
                  <option value="completed">Publicado ✓</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                {editingContent ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteContent(editingContent.id)}
                    className="text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700 gap-1"
                  >
                    <Trash2 size={13} />
                    Eliminar
                  </Button>
                ) : (
                  <div />
                )}

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setContentModalOpen(false)}
                    className="text-xs"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={savingContent}
                    className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {savingContent ? <Loader2 size={13} className="animate-spin" /> : 'Guardar'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Modal: Crear / Editar Acción de Marketing ─── */}
      {actionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900 text-sm">
                {editingAction ? 'Editar Acción de Marketing' : 'Nueva Acción de Marketing'}
              </h3>
              <button
                onClick={() => setActionModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveAction} className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Título de la Acción *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Sorteo Día de la Madre con marca aliada"
                  value={actionForm.title}
                  onChange={(e) => setActionForm({ ...actionForm, title: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Tipo de Acción
                  </label>
                  <select
                    value={actionForm.action_type}
                    onChange={(e) => setActionForm({ ...actionForm, action_type: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 bg-white"
                  >
                    <option value="Campaña">Campaña</option>
                    <option value="Sorteo">Sorteo</option>
                    <option value="Colaboración">Colaboración</option>
                    <option value="Ads / Pauta">Ads / Pauta</option>
                    <option value="Email Marketing">Email Marketing</option>
                    <option value="Lanzamiento">Lanzamiento</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">
                    Fecha Límite
                  </label>
                  <input
                    type="date"
                    value={actionForm.due_date}
                    onChange={(e) => setActionForm({ ...actionForm, due_date: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Descripción u Objetivos
                </label>
                <textarea
                  rows={3}
                  placeholder="Objetivos específicos de la acción, presupuesto estimado o requisitos..."
                  value={actionForm.description}
                  onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Estado
                </label>
                <select
                  value={actionForm.status}
                  onChange={(e) =>
                    setActionForm({
                      ...actionForm,
                      status: e.target.value as MarketingAction['status'],
                    })
                  }
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 bg-white"
                >
                  <option value="planned">Planificada</option>
                  <option value="in_progress">En Curso</option>
                  <option value="completed">Completada ✓</option>
                  <option value="cancelled">Cancelada</option>
                </select>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                {editingAction ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAction(editingAction.id)}
                    className="text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700 gap-1"
                  >
                    <Trash2 size={13} />
                    Eliminar
                  </Button>
                ) : (
                  <div />
                )}

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setActionModalOpen(false)}
                    className="text-xs"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={savingAction}
                    className="text-xs bg-pink-600 hover:bg-pink-700 text-white"
                  >
                    {savingAction ? <Loader2 size={13} className="animate-spin" /> : 'Guardar'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
